import { Pagination as AntdPagination } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useMedia } from 'react-use';
import { PAGE_SIZE_OPTIONS } from 'utils/pagination';
import './index.scss';

/**
 * The pager for the views that antd does not draw one for: the mobile card
 * list, and the ads grid. Both used to hand their `pagination` straight to
 * antd's List, which always slices the array it is given - so with a
 * server-paginated screen holding only the current page, page two sliced past
 * the end and came up empty.
 *
 * Reports changes as `{ current, pageSize }`, the same shape antd's Table
 * reports, so a screen can use one handler for both layouts.
 */
const Pagination = ({ current, pageSize, total, onChange, showSizeChanger = true, hideOnSinglePage = true }) => {
  const { t } = useTranslation('common');
  // Below this the full pager wraps onto three lines; antd has narrow variants.
  const isNarrow = useMedia('(max-width: 575px)');

  if (!total || (hideOnSinglePage && total <= pageSize)) return null;

  return (
    <AntdPagination
      className="custom-pagination"
      current={current}
      pageSize={pageSize}
      total={total}
      /* rc-pagination fires this for a page change and for a size change
         alike, with `current` already clamped to the last page that still
         exists at the new size - so one handler covers both. */
      onChange={(page, size) => onChange({ current: page, pageSize: size })}
      showSizeChanger={showSizeChanger}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      size={isNarrow ? 'small' : 'default'}
      // Fewer page buttons around the current one, so the row fits a phone.
      showLessItems={isNarrow}
      locale={{ items_per_page: t('per-page') }}
    />
  );
};

Pagination.propTypes = {
  current: PropTypes.number,
  pageSize: PropTypes.number,
  total: PropTypes.number,
  onChange: PropTypes.func,
  showSizeChanger: PropTypes.bool,
  hideOnSinglePage: PropTypes.bool,
};

export default Pagination;
